import type { ParsedQs } from 'qs';

import { NotFoundError, RedirectError } from '#src/shared/error';

import { buildComboUrl, buildSocialStarterUrl } from '#src/app/pages/psf/promoteOrShare/shared/utils';
import type { PsfFirstStepRoute } from '#src/app/pages/psf/promoteOrShare/shared/utils';
import type { UtilityContextWithTracker } from '#src/app/shared/contexts/asyncData';
import { getQueryStringSingleValue } from '#src/app/shared/utils/query';

import type { PsfPromoteOrShareDataQuery, PsfPromoteOrShareDataQueryVariables } from './api.graphql';
import { PsfPromoteOrShareData } from './api.graphql';
import { handleRedirect } from './handleRedirect';
import { normalizeData } from './normalizeData';
import { normalizeSocialStarterData } from './normalizeSocialStarterData';
import { getRecruiter } from './recruiter';
import type { PsfPromoteOrShareRecruiterQuery } from './recruiter.graphql';
import type { PsfSocialStarterDataQuery, PsfSocialStarterDataQueryVariables } from './socialStarter.graphql';
import { PsfSocialStarterData } from './socialStarter.graphql';
import { fetchSocialStarterEligibility } from './socialStarterEligibility';
import type { Petition, PromotionStats, PsfPromoteOrSharePageData } from './types';
import { PsfPromoteOrSharePageDataError } from './types';

export type { PsfPromoteOrSharePageData };

type FetchOptions = {
	recruiterId?: string;
};

type Params = {
	slug: string;
	route: PsfFirstStepRoute;
	query: ParsedQs;
};

type Recruiter = NonNullable<PsfPromoteOrShareRecruiterQuery['recruiter']> | undefined;

async function fetchRecruiter(
	utilityContext: UtilityContextWithTracker,
	{ recruiterId }: FetchOptions,
): Promise<Recruiter> {
	return recruiterId ? getRecruiter(utilityContext, recruiterId) : undefined;
}

/** Shared post-fetch checks: missing petition -> NotFoundError, missing promotion stats -> data error. */
function assertValidPageData<DATA extends { petition: Petition | null }>(
	data: DATA | null | undefined,
): asserts data is DATA & { petition: Petition & { promotionStats: PromotionStats } } {
	if (!data?.petition) {
		throw new NotFoundError();
	}

	if (!data.petition.promotionStats) {
		throw new PsfPromoteOrSharePageDataError('psf_promote_or_share_page_missing_promotion_stats');
	}
}

/**
 * Must finish before either main query starts: those queries' treat:true fields enroll the
 * visitor as they resolve, and a request about to be redirected must never reach that.
 */
async function redirectIfWrongRoute(
	utilityContext: UtilityContextWithTracker,
	{ route, query, slug }: Pick<Params, 'route' | 'query' | 'slug'>,
): Promise<void> {
	const eligibility = await fetchSocialStarterEligibility(utilityContext, slug);

	if (route === 'combo' && eligibility.eligible) {
		void utilityContext.track('combo_psf_social_starter_first_step', { slug: eligibility.slug });

		throw new RedirectError({
			url: buildSocialStarterUrl(eligibility.slug, query),
			reason: 'social_starter_first_step',
		});
	}

	if (route === 'socialStarter' && !eligibility.eligible) {
		void utilityContext.track('combo_psf_social_starter_ineligible', { slug: eligibility.slug });

		throw new RedirectError({
			url: buildComboUrl(eligibility.slug, query),
			reason: 'social_starter_ineligible',
		});
	}
}

export async function getPsfPromoteOrSharePageData(
	utilityContext: UtilityContextWithTracker,
	params: Params,
	options: FetchOptions,
): Promise<PsfPromoteOrSharePageData> {
	const { route, query, slug } = params;

	// Skipped for a visitor just bounced from Social Starter, so the two routes can't loop.
	if (route !== 'combo' || getQueryStringSingleValue(query, 'redirect_reason') !== 'social_starter_ineligible') {
		await redirectIfWrongRoute(utilityContext, { route, query, slug });
	}

	if (route === 'combo') {
		const [petitionResult, recruiter] = await Promise.all([
			utilityContext.gql.fetch<PsfPromoteOrShareDataQuery, PsfPromoteOrShareDataQueryVariables>({
				query: PsfPromoteOrShareData,
				variables: { slugOrId: slug },
				rejectOnError: true,
			}),
			fetchRecruiter(utilityContext, options),
		]);
		const { data } = petitionResult;

		assertValidPageData(data);
		handleRedirect(utilityContext.track, {
			petition: data.petition,
			viewer: data.viewer,
			fcmValues: data.featureConfigs,
		});

		// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
		const { psfFeaturedSpikingPetition, ...petition } = data.petition;

		return normalizeData(utilityContext, {
			petition,
			featuredSpikingPetition: psfFeaturedSpikingPetition,
			promotionStats: data.petition.promotionStats,
			viewer: data.viewer,
			experiments: data.experiments,
			recruiter,
			featureConfigs: data.featureConfigs,
		});
	}

	const [petitionResult, recruiter] = await Promise.all([
		utilityContext.gql.fetch<PsfSocialStarterDataQuery, PsfSocialStarterDataQueryVariables>({
			query: PsfSocialStarterData,
			variables: { slugOrId: slug },
			rejectOnError: true,
		}),
		fetchRecruiter(utilityContext, options),
	]);
	const { data } = petitionResult;

	assertValidPageData(data);
	handleRedirect(utilityContext.track, {
		petition: data.petition,
		viewer: data.viewer,
		fcmValues: data.featureConfigs,
	});

	return normalizeSocialStarterData(utilityContext, {
		petition: data.petition,
		promotionStats: data.petition.promotionStats,
		viewer: data.viewer,
		experiments: data.experiments,
		recruiter,
		featureConfigs: data.featureConfigs,
	});
}
